// Typed fetch wrappers for the dashboard endpoints (Plan 05-02).
//
// Each wrapper takes an AbortSignal so TanStack Query can cancel in-flight
// requests on key change (FILT-04 mitigation: stale responses must never
// overwrite fresh ones — T-05-15 / Pitfall 6).
//
// Sort note (RANK-08): the server orders /api/rankings by composite desc by
// default. When `filters.sort = dog | walk_ent | recency` the server already
// re-sorts (per Plan 05-02 sortPayload). The client trusts the server order
// and does not re-sort here.

import type {
  FilterState,
  JobDetailDTO,
  JobListDTO,
  MapDataResponse,
  PropertyDetailDTO,
  RankedPropertyDTO,
  StatsDTO,
  SubmitJobResponse,
  Property,
} from '@/types/db';
import { serializeFilters } from './filterDefaults';

class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

async function jsonOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      if (body && typeof body === 'object' && 'error' in body) detail = String(body.error);
    } catch {
      /* response not JSON — fall through */
    }
    throw new ApiError(
      `${label} request failed: ${res.status}${detail ? ` (${detail})` : ''}`,
      res.status,
    );
  }
  return (await res.json()) as T;
}

/**
 * GET /api/rankings — ranked property list filtered by radius/sort/county/recency/cats.
 * AbortController-safe via the `signal` parameter (FILT-04).
 */
export async function fetchRankings(
  filters: FilterState,
  signal: AbortSignal | undefined,
): Promise<RankedPropertyDTO[]> {
  const sp = serializeFilters(filters);
  const res = await fetch(`/api/rankings?${sp.toString()}`, { signal });
  return jsonOrThrow<RankedPropertyDTO[]>(res, 'Rankings');
}

/**
 * Server response shape for /api/stats (Plan 05-02).
 * Server uses snake_case `total_properties / avg_composite / top_property /
 * freshest_property` while the UI's StatsDTO contract uses `total / avg_fit /
 * top_fit / freshest`. We normalize at the boundary so components consume the
 * shape declared in `types/db.ts`.
 */
interface StatsApiResponse {
  total_properties: number;
  shown: number;
  avg_composite: number | null;
  top_property: { id: number; address: string; composite: number } | null;
  freshest_property: { id: number; address: string; created_at: string } | null;
}

/** GET /api/stats — normalize server snake_case to StatsDTO. */
export async function fetchStats(signal: AbortSignal | undefined): Promise<StatsDTO> {
  const res = await fetch('/api/stats', { signal });
  const raw = await jsonOrThrow<StatsApiResponse>(res, 'Stats');
  return {
    total: raw.total_properties,
    avg_fit: raw.avg_composite,
    top_fit: raw.top_property
      ? { id: raw.top_property.id, address: raw.top_property.address, composite: raw.top_property.composite }
      : null,
    freshest: raw.freshest_property
      ? {
          id: raw.freshest_property.id,
          address: raw.freshest_property.address,
          created_at: raw.freshest_property.created_at,
        }
      : null,
  };
}

/**
 * Server returns `Property` rows augmented with `is_incomplete` flag derived from
 * null sub-scores (Plan 05-02). Plan 05-05 only needs the county/lat/lon subset
 * (for FILT-06 multi-select source list + future map work) but we keep the full
 * shape to avoid a second round-trip when Phase 06 properties listing wires up.
 */
export interface PropertiesApiRow extends Property {
  is_incomplete: boolean;
}

/** GET /api/properties — bare array; X-Distinct-Counties header attached server-side. */
export async function fetchProperties(
  signal: AbortSignal | undefined,
): Promise<PropertiesApiRow[]> {
  const res = await fetch('/api/properties', { signal });
  return jsonOrThrow<PropertiesApiRow[]>(res, 'Properties');
}

export { ApiError };

// ============================================================
// Phase 06 — Property Detail
// ============================================================

/**
 * Sentinel error thrown by `fetchPropertyDetail` when the server returns 404.
 * Lets the route + TanStack Query discriminate "missing property" from a
 * generic network failure without parsing string messages.
 *
 * Plan 06-01 wires `__amenity_refresh__` and unknown ids to a real 404 from
 * the Express handler; the SPA never renders the sentinel as a real card,
 * so direct-URL navigation to it lands here too.
 */
export class PropertyNotFoundError extends Error {
  constructor(public propertyId: number) {
    super(`Property ${propertyId} not found`);
    this.name = 'PropertyNotFoundError';
  }
}

/**
 * GET /api/properties/:id — single round-trip detail payload (DETAIL-02).
 * AbortController-safe; throws `PropertyNotFoundError` on 404 and a generic
 * `Error` on any other non-2xx response. Returns the parsed DTO on success.
 */
export async function fetchPropertyDetail(
  id: number,
  signal?: AbortSignal,
): Promise<PropertyDetailDTO> {
  const res = await fetch(
    `/api/properties/${encodeURIComponent(String(id))}`,
    { signal },
  );
  if (res.status === 404) {
    throw new PropertyNotFoundError(id);
  }
  if (!res.ok) {
    throw new ApiError(`HTTP ${res.status}: ${res.statusText}`, res.status);
  }
  return (await res.json()) as PropertyDetailDTO;
}

/**
 * POST /api/properties/:id/images/:imageId/favorite — toggles the favorite
 * flag on a single property image. Server returns the new `is_favorite`
 * (0 / 1) integer. v1.0 endpoint, unchanged trust boundary; called by
 * <HeroGallery> for the heart-toggle interaction. The route shell handles
 * optimistic-rollback + toast in `<PropertyDetail>` via `onFavoriteError`.
 */
export async function togglePhotoFavorite(
  propertyId: number,
  imageId: number,
): Promise<{ id: number; is_favorite: number }> {
  const res = await fetch(
    `/api/properties/${encodeURIComponent(String(propertyId))}/images/${encodeURIComponent(String(imageId))}/favorite`,
    { method: 'POST' },
  );
  if (!res.ok) {
    throw new ApiError(`Photo favorite request failed: ${res.status}`, res.status);
  }
  return (await res.json()) as { id: number; is_favorite: number };
}

// ============================================================
// Phase 07 — Jobs UI wrappers (JOBS-01, JOBS-02, JOBS-04)
// ============================================================

/**
 * GET /api/jobs — list view payload. AbortController-safe.
 * Sentinel-excluded server-side; sorted by created_at DESC.
 * Each row carries `agent_progress` keyed by the 7 UI-canonical agent names.
 */
export async function fetchJobs(
  signal?: AbortSignal,
): Promise<JobListDTO[]> {
  const res = await fetch('/api/jobs', { signal });
  return jsonOrThrow<JobListDTO[]>(res, 'Jobs');
}

/**
 * GET /api/jobs/:id/status — detail view payload (polled at 3s by useJobStatus).
 * Throws `ApiError(404)` when the job no longer exists so the route can render
 * a not-found state without re-parsing error strings.
 */
export async function fetchJobStatus(
  id: number,
  signal?: AbortSignal,
): Promise<JobDetailDTO> {
  const res = await fetch(
    `/api/jobs/${encodeURIComponent(String(id))}/status`,
    { signal },
  );
  if (res.status === 404) {
    throw new ApiError(`Job ${id} not found`, 404);
  }
  return jsonOrThrow<JobDetailDTO>(res, 'Job status');
}

/**
 * POST /api/jobs — submit form. Returns the new job's id for navigation
 * (the SubmitForm route uses `useNavigate('/app/jobs/<id>', { replace: true })`).
 */
export async function submitJob(
  address: string,
): Promise<SubmitJobResponse> {
  const res = await fetch('/api/jobs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address }),
  });
  return jsonOrThrow<SubmitJobResponse>(res, 'Submit job');
}

// ============================================================
// Phase 08 — Map page wrappers (MAP-01)
// ============================================================

/**
 * GET /api/map-data — full amenity + property payload for the /app/map view.
 * Server returns the existing v1.0 envelope (sentinel excluded, lat/lon non-null).
 * AbortController-safe via the `signal` parameter.
 */
export async function fetchMapData(
  signal?: AbortSignal,
): Promise<MapDataResponse> {
  const res = await fetch('/api/map-data', { signal });
  return jsonOrThrow<MapDataResponse>(res, 'Map data');
}

/**
 * GET /api/rankings?radius={radiusMiles} — minimal map-popup variant.
 *
 * Phase 08 Plan 04 (gap closure): the map's property popup needs composite
 * score + per-category neighbor counts at the user's current radius. The
 * dashboard's `fetchRankings` is keyed by the full FilterState (sort/
 * county/recency); the map ignores all of those — only `radius` matters
 * for "what's the score and how many amenities are nearby right now".
 *
 * Server returns the full ranked list (default sort by composite desc per
 * RANK-08); the client converts it to a Map<property_id, row> for O(1)
 * popup lookup. AbortController-safe via the `signal` parameter.
 */
export async function fetchMapRankings(
  radiusMiles: number,
  signal: AbortSignal | undefined,
): Promise<RankedPropertyDTO[]> {
  const res = await fetch(
    `/api/rankings?radius=${encodeURIComponent(radiusMiles.toString())}`,
    { signal },
  );
  return jsonOrThrow<RankedPropertyDTO[]>(res, 'Map rankings');
}

// ============================================================
// Phase 09 — Admin endpoints (ADMIN-02, ADMIN-03)
// ============================================================
import type {
  AdminAmenitiesDTO,
  AdminRefreshScope,
  AdminRefreshResponse,
  AdminRecomputeAcceptedResponse,
  RecomputeStatusDTO,
  PhotoCollectResponse,
  PhotoJobStatusDTO,
  CreditBalanceDTO,
} from '@/types/db';

/** GET /api/admin/amenities — county × category matrix (ADMIN-02). */
export async function fetchAdminAmenities(
  signal?: AbortSignal,
): Promise<AdminAmenitiesDTO> {
  const res = await fetch('/api/admin/amenities', { signal });
  return jsonOrThrow<AdminAmenitiesDTO>(res, 'Admin amenities');
}

/**
 * POST /api/admin/refresh — fire a refresh job. Server validates scope +
 * county/category against ALL_COUNTIES / ADMIN_REFRESH_CATEGORIES (T-04-08 /
 * T-04-09). On 409 (concurrency), throws ApiError with body carrying
 * `{ running_job_id }` so the caller can render an inline banner.
 */
export async function triggerRefresh(
  payload: AdminRefreshScope,
  signal?: AbortSignal,
): Promise<AdminRefreshResponse> {
  const res = await fetch('/api/admin/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError('Refresh already in progress', 409, body);
  }
  return jsonOrThrow<AdminRefreshResponse>(res, 'Admin refresh');
}

/**
 * POST /api/admin/recompute-rankings — RANK-04 trigger #3. Returns 202
 * Accepted on success (in-flight); 409 surfaces existing run via ApiError.
 */
export async function triggerRecompute(
  signal?: AbortSignal,
): Promise<AdminRecomputeAcceptedResponse> {
  const res = await fetch('/api/admin/recompute-rankings', {
    method: 'POST',
    signal,
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError('Recompute already running', 409, body);
  }
  // Server returns 202 Accepted, jsonOrThrow accepts any 2xx.
  return jsonOrThrow<AdminRecomputeAcceptedResponse>(res, 'Recompute trigger');
}

/** GET /api/admin/recompute-rankings/status — polled at 3s while running. */
export async function fetchRecomputeStatus(
  signal?: AbortSignal,
): Promise<RecomputeStatusDTO> {
  const res = await fetch('/api/admin/recompute-rankings/status', { signal });
  return jsonOrThrow<RecomputeStatusDTO>(res, 'Recompute status');
}

// ============================================================
// Phase 11 Plan 04 — Tiered photo collection fetchers
// ============================================================

/**
 * POST /api/properties/:id/photos/collect
 * Starts a photo collection job for the given source tier.
 * Throws ApiError on non-2xx — carries 409 body so the UI can show
 * "collection in progress" inline (with the running photo_job_id).
 */
export async function collectPhotos(
  id: number,
  source: 'streetview' | 'zillow' | 'deep',
  signal?: AbortSignal,
): Promise<PhotoCollectResponse> {
  const res = await fetch(`/api/properties/${encodeURIComponent(String(id))}/photos/collect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source }),
    signal,
  });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* not JSON */ }
    throw new ApiError(
      `collectPhotos request failed: ${res.status}`,
      res.status,
      body,
    );
  }
  return (await res.json()) as PhotoCollectResponse;
}

/**
 * GET /api/properties/:id/photos/status
 * Polls the latest photo_jobs row for the property.
 * Returns `{ status: 'idle' }` when no job has been started.
 */
export async function fetchPhotoJobStatus(
  id: number,
  signal?: AbortSignal,
): Promise<PhotoJobStatusDTO> {
  const res = await fetch(
    `/api/properties/${encodeURIComponent(String(id))}/photos/status`,
    { signal },
  );
  return jsonOrThrow<PhotoJobStatusDTO>(res, 'Photo job status');
}

/**
 * GET /api/credits
 * Returns the remaining Firecrawl credit balance.
 * Used by the Deep Dive confirm dialog to show cost visibility (D-3/D-9).
 * `balance` is null when the server cannot parse the firecrawl output —
 * the UI should treat null as "balance unknown, proceed with caution".
 */
export async function fetchCreditBalance(
  signal?: AbortSignal,
): Promise<CreditBalanceDTO> {
  const res = await fetch('/api/credits', { signal });
  return jsonOrThrow<CreditBalanceDTO>(res, 'Credit balance');
}
